import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { TextInput, toast } from '@etherisc/ui-kit';
import { PrimaryButton, NeutralButton } from './buttons';
import { userFormSchema, type UserFormData } from '../schemas/userSchema';
import { User } from '../types/user';
import { PermissionGate } from './PermissionGate';
import { FormCard } from './cards';
import { EmailStatusIndicator } from './EmailStatusIndicator';
import { authService } from '../services/authService';
import { useAuth } from '../hooks/useAuth';

interface UserFormProps {
  mode: 'create' | 'edit';
  user?: User;
  onSubmit: (data: UserFormData) => Promise<void>;
  loading?: boolean;
  availableRoles?: string[];
  availableOrganizations?: Array<{ id: string; name: string }>;
  organizationsError?: string | null;
}

export function UserForm({ 
  mode, 
  user, 
  onSubmit, 
  loading = false,
  availableRoles = ['admin', 'user', 'manager'],
  availableOrganizations = [],
  organizationsError = null
}: UserFormProps) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  // Initialize selected roles
  // For create mode with single available role: auto-select it
  // For edit mode or multiple roles: use user's existing roles or empty
  const initialRoles = mode === 'create' && availableRoles.length === 1 
    ? availableRoles 
    : (user?.roles || []);
  
  const [selectedRoles, setSelectedRoles] = useState<string[]>(initialRoles);
  const [resendingVerification, setResendingVerification] = useState(false);
  
  // Determine default organization
  const defaultOrganizationId = mode === 'create' 
    ? (currentUser?.organization?.id || '') // Use current user's org for create mode
    : (user?.organization?.id || ''); // Use editing user's org for edit mode
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      roles: initialRoles,
      organizationId: defaultOrganizationId,
      isActive: user?.isActive ?? true
    }
  });

  // Sync form state with pre-selected roles (handles create mode with single role)
  useEffect(() => {
    if (initialRoles.length > 0) {
      setValue('roles', initialRoles);
    }
  }, [initialRoles, setValue]);

  // Auto-select organization if only one is available (only in create mode)
  useEffect(() => {
    if (mode === 'create' && availableOrganizations.length === 1) {
      setValue('organizationId', availableOrganizations[0].id);
    }
  }, [availableOrganizations, mode, setValue]);

  const handleFormSubmit = async (data: UserFormData) => {
    try {
      const formData = { ...data, roles: selectedRoles };
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  const handleResendVerification = async () => {
    if (!user?.id) return;
    
    try {
      setResendingVerification(true);
      await authService.resendVerificationEmailForUser(user.id);
      toast.success('Verification email sent', {
        description: 'A new verification email has been sent to the user.'
      });
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      toast.error('Failed to send verification email', {
        description: 'Please try again later.'
      });
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user?.email) return;
    
    try {
      setResendingVerification(true); // Reuse loading state
      await authService.requestPasswordReset(user.email);
      toast.success('Password reset email sent', {
        description: 'A password reset email has been sent to the user.'
      });
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      toast.error('Failed to send password reset email', {
        description: 'Please try again later.'
      });
    } finally {
      setResendingVerification(false);
    }
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    if (checked) {
      setSelectedRoles(prev => [...prev, role]);
    } else {
      setSelectedRoles(prev => prev.filter(r => r !== role));
    }
    setValue('roles', checked ? [...selectedRoles, role] : selectedRoles.filter(r => r !== role));
  };

  // Determine if we should show auto-selection label
  const shouldShowAutoSelection = mode === 'create' && availableOrganizations.length === 1;

  return (
    <FormCard title={mode === 'create' ? 'Create New User' : 'Edit User'}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
            {/* Basic Information Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 gap-6">
                <TextInput
                  id="name"
                  label="Full Name"
                  {...register('name')}
                  error={errors.name?.message}
                />

                <TextInput
                  id="email"
                  label="Email Address"
                  type="email"
                  {...register('email')}
                  error={errors.email?.message}
                />

                {/* Email Verification Status - only show in edit mode */}
                {mode === 'edit' && user && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Email Verification Status
                    </label>
                    <EmailStatusIndicator
                      user={user}
                      showResendButton={true}
                      onResendVerification={handleResendVerification}
                      onSendPasswordReset={handleSendPasswordReset}
                      resending={resendingVerification}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Roles and Permissions Section */}
            <PermissionGate permission="users:create">
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                  Roles and Permissions
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Roles (Select at least one)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {availableRoles.map((role) => (
                      <label key={role} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role)}
                          onChange={(e) => handleRoleChange(role, e.target.checked)}
                          disabled={loading || isSubmitting}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{role}</span>
                      </label>
                    ))}
                  </div>
                  {errors.roles && (
                    <p className="mt-1 text-sm text-red-600">{errors.roles.message}</p>
                  )}
                </div>
              </div>
            </PermissionGate>

            {/* Organization Section */}
            <PermissionGate permission="users:create">
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                  Organization
                </h3>
                
                {organizationsError ? (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error Loading Organizations</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{organizationsError}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : availableOrganizations.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">No Organizations Available</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>No organizations are available. Please create an organization first.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="organizationId" className="block text-sm font-medium text-gray-700 mb-1">
                      Organization {shouldShowAutoSelection && '(Auto-selected)'}
                    </label>
                    <select
                      id="organizationId"
                      {...register('organizationId')}
                      disabled={loading || isSubmitting || availableOrganizations.length <= 1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      <option value="">Select an organization</option>
                      {availableOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    {errors.organizationId && (
                      <p className="mt-1 text-sm text-red-600">{errors.organizationId.message}</p>
                    )}
                    {shouldShowAutoSelection && (
                      <p className="mt-1 text-sm text-gray-500">
                        This organization has been automatically selected as it's the only one available.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </PermissionGate>

            {/* Status Section */}
            <PermissionGate permission="users:update">
              {mode === 'edit' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Status
                  </h3>
                  
                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        {...register('isActive')}
                        disabled={loading || isSubmitting}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">User is active</span>
                    </label>
                  </div>
                </div>
              )}
            </PermissionGate>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <NeutralButton
                onClick={handleCancel}
                disabled={loading || isSubmitting}
              >
                Cancel
              </NeutralButton>
              <PrimaryButton
                type="submit"
                disabled={selectedRoles.length === 0}
                loading={loading || isSubmitting}
                loadingText="Saving..."
              >
                {mode === 'create' ? 'Create User' : 'Update User'}
              </PrimaryButton>
            </div>
          </form>
    </FormCard>
  );
} 